import type { ObjectId } from 'mongodb';

import type { Meta } from './Entities';

export interface CongaSigningUrl {
  signer: string;
  url: string;
}

// Closed lists from Conga Payload Standard v6; Conga rejects any other value.
export const CONGA_PROGRAM_TYPES = [
  'doePr',
  'energyBackup',
  'generator',
  'geothermal',
  'hvac',
  'newCommunity',
  'newHomes',
  'consumerSolar',
  'solar',
  'waterHeater',
] as const;
export type CongaProgramType = (typeof CONGA_PROGRAM_TYPES)[number];

export const CONGA_FINANCE_TYPES = ['ppa', 'lease', 'loan'] as const;
export type CongaFinanceType = (typeof CONGA_FINANCE_TYPES)[number];

export const CONGA_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const;
export type CongaState = (typeof CONGA_STATES)[number];

export const CONGA_LANGUAGES = ['English', 'Spanish'] as const;
export type CongaLanguage = (typeof CONGA_LANGUAGES)[number];

export const CONGA_LANGUAGE_WAIVERS = ['None', 'Spanish'] as const;
export type CongaLanguageWaiver = (typeof CONGA_LANGUAGE_WAIVERS)[number];

// Array order is the order signers are sent in.
export const CONGA_SIGNER_ROLES = [
  'PrimaryApplicant',
  'CoApplicant',
  'LicensedInstaller',
  'UtilityAccountHolder',
  'Salesperson',
] as const;
export type CongaSignerRole = (typeof CONGA_SIGNER_ROLES)[number];

// Conga's rules engine selects the package and templates from ProgramType, FinanceType, State, Market and Language.
export interface CongaDocumentPackage {
  Name?: string;
  ExternalId: string;
  ContractId: string;
  QuoteId: string;
  ProgramType: CongaProgramType;
  FinanceType: CongaFinanceType;
  State: CongaState;
  StateName: string;
  Market: string;
  Language: CongaLanguage;
  LanguageWaiver: CongaLanguageWaiver;
  VaultOnCompletion: boolean;
  VaultProvider: 'eOriginal';
  LoanId?: string;
  LoanAmount?: string;
  Lender?: string;
}

export interface CongaSigner {
  Role: CongaSignerRole;
  FirstName: string;
  LastName: string;
  Email: string;
  RedirectUrl: string;
}

export interface CongaEquipmentItem {
  Name: string;
  Manufacturer: string;
  Model: string;
  Quantity: number;
  Type: string;
  'System Category': string;
}

// Merge-field values keyed by exact, case-sensitive Conga field names; every field is always sent.
export type CongaContract = Record<string, string | number | boolean | null | CongaEquipmentItem[]>;

// Conga Payload Standard v6.
export interface CongaPackagePayload {
  DocumentPackage: CongaDocumentPackage;
  SigningMethod: '$Parallel';
  Signers: CongaSigner[];
  Contract: CongaContract;
}

export interface CongaGenerateRequest {
  requestId: string;
  payload: CongaPackagePayload;
}

export interface CongaGenerateResult {
  requestId: string;
  mergeRequestId: string;
  mergeDocumentIds: string[];
  status: string;
}

// MergeDocumentNotify statuses, in the order a document moves through them; Error and Rejected are terminal.
export const CONGA_MERGE_DOCUMENT_STATUSES = ['Preview', 'Merged', 'Completed', 'Error', 'Rejected'] as const;
export type CongaMergeDocumentStatus = (typeof CONGA_MERGE_DOCUMENT_STATUSES)[number];

export interface CongaMergeDocument {
  status: CongaMergeDocumentStatus;
  previewUrl?: string;
  /** Presigned, so it expires. */
  downloadUrl?: string;
  error?: string;
  updatedAt: string;
}

/** One webhook delivery from either Conga endpoint, as it applied to a test envelope row. */
export interface CongaTestEnvelopeEvent {
  source: 'mergeDocument' | 'action';
  /** The webhook log's inboundEvent, e.g. `Merged`, `Sign.Error`, `Sign.PACKAGE_CREATE`. */
  event: string;
  mergeDocumentId: string;
  error?: string;
  receivedAt: string;
  /** The `webhookLogs` document holding the raw body; absent on events recorded before it was tracked. */
  webhookLogId?: string;
}

export enum CongaTestEnvelopeStatus {
  pending = 'pending',
  submitting = 'submitting',
  awaitingSigningUrls = 'awaitingSigningUrls',
  succeeded = 'succeeded',
  failed = 'failed',
}

export interface CongaTestEnvelope {
  id: ObjectId;
  /** UUID shared by all rows from one upload. */
  batchId: string;
  /** 1-based data row index. */
  rowNumber: number;
  label?: string;
  csvRow: Record<string, string>;
  /** Absent when row validation failed. */
  payload?: CongaPackagePayload;
  status: CongaTestEnvelopeStatus;
  result?: CongaGenerateResult;
  /** Latest MergeDocumentNotify state, keyed by mergeDocumentId. */
  mergeDocuments?: Record<string, CongaMergeDocument>;
  /** Every matched webhook delivery, oldest first; capped to the most recent deliveries. */
  events?: CongaTestEnvelopeEvent[];
  signingUrls?: CongaSigningUrl[];
  error?: string;
  submittedAt?: string;
  /** Unique per claim attempt; proves which connection won the pending-to-submitting transition. */
  claimId?: string;
  completedAt?: string;
  createdByUserId: string;
  meta?: Meta;
}

export interface CongaTestBatchSummary {
  batchId: string;
  createdAt: string;
  createdByUserId: string;
  createdByName: string;
  label?: string;
  rowCount: number;
  succeededCount: number;
  failedCount: number;
}

export interface ListCongaTestBatchesResponse {
  batches: CongaTestBatchSummary[];
}

export type CongaTestBatchEvent =
  | { type: 'batch.snapshot'; rows: CongaTestEnvelope[] }
  | { type: 'row.updated'; row: CongaTestEnvelope }
  | { type: 'batch.finished' }
  | { type: 'batch.error'; message: string };
